import React, { useState, useEffect } from 'react';
import { Button, Input, Switch } from 'antd';
import { MessageSquare, Plus, ChevronDown, ChevronUp, Play, Pause, Trash } from 'lucide-react';
import ShortUniqueId from 'short-unique-id';

import textingSample from '../../../assets/sounds/samples/texting-workflow-sample.mp3';
import { useAudioPlayer } from './hooks/useAudioPlayer';

const { TextArea } = Input;

interface TextingWorkflow {
	id: string;
	description: string;
	textMessage: string;
	shortURLEnabled: boolean;
	askForPermissionOnText: boolean;
	postExecutionPrompt: string;
}

interface OnboardingData {
	welcomeCompleted: boolean;
	businessName: string;
	phoneNumber: string;
	addresses: any[];
	businessHours: any;
	pricing: any[];
	faqs: any[];
	additionalInfo: string;
	callWorkflows: any[];
	textingWorkflows: TextingWorkflow[];
}

interface TextingWorkflowStageProps {
	onboardingData: OnboardingData;
	updateOnboardingData: (updates: Partial<OnboardingData>) => void;
}

const TextingWorkflowStage: React.FC<TextingWorkflowStageProps> = ({
	onboardingData,
	updateOnboardingData,
}) => {
	const [expandedWorkflow, setExpandedWorkflow] = useState<string | null>(() => {
		if (onboardingData.textingWorkflows && onboardingData.textingWorkflows.length > 0) {
			return onboardingData.textingWorkflows[0].id;
		}
		return null;
	});

	const [showValidationErrors, setShowValidationErrors] = useState(false);

	// Auto-create first workflow if none exist
	useEffect(() => {
		if (!onboardingData.textingWorkflows || onboardingData.textingWorkflows.length === 0) {
			const firstWorkflow: TextingWorkflow = {
				id: new ShortUniqueId({ length: 10 }).rnd(),
				description: '',
				textMessage: '',
				shortURLEnabled: true,
				askForPermissionOnText: false,
				postExecutionPrompt: '',
			};

			updateOnboardingData({
				textingWorkflows: [firstWorkflow],
			});

			// Expand the newly created workflow
			setExpandedWorkflow(firstWorkflow.id);
		}
	}, [onboardingData.textingWorkflows, updateOnboardingData]);

	const { audioRef, isPlaying, currentTime, duration, isLoading, togglePlayPause, formatTime } =
		useAudioPlayer({ audioSrc: textingSample });

	const addWorkflow = () => {
		const newWorkflow: TextingWorkflow = {
			id: new ShortUniqueId({ length: 10 }).rnd(),
			description: '',
			textMessage: '',
			shortURLEnabled: true,
			askForPermissionOnText: false,
			postExecutionPrompt: '',
		};

		updateOnboardingData({
			textingWorkflows: [...(onboardingData.textingWorkflows || []), newWorkflow],
		});

		// Expand the newly added workflow
		setExpandedWorkflow(newWorkflow.id);
	};

	const removeWorkflow = (id: string) => {
		updateOnboardingData({
			textingWorkflows: onboardingData.textingWorkflows?.filter((w) => w.id !== id),
		});
	};

	const updateWorkflow = (id: string, field: keyof TextingWorkflow, value: any) => {
		updateOnboardingData({
			textingWorkflows: onboardingData.textingWorkflows?.map((w) =>
				w.id === id ? { ...w, [field]: value } : w,
			),
		});
	};

	const toggleWorkflowExpanded = (workflowId: string) => {
		setExpandedWorkflow(expandedWorkflow === workflowId ? null : workflowId);
	};

	// Listen for validation attempts from parent
	useEffect(() => {
		const handleValidation = () => {
			setShowValidationErrors(true);
		};

		window.addEventListener('trigger-validation', handleValidation);
		return () => window.removeEventListener('trigger-validation', handleValidation);
	}, []);

	return (
		<div className="max-w-2xl mx-auto px-4">
			{/* Hidden audio element */}
			<audio ref={audioRef} src={textingSample} />

			{/* Friendly Header */}
			<div className="text-center mb-6">
				<h2 className="text-2xl font-bold text-blue-600 mb-3">Set Up Text Messages 💬</h2>
				<p className="text-gray-600 text-sm leading-relaxed">
					Help your AI receptionist know when to send text messages to callers
				</p>
			</div>

			{/* Sample Text Response */}
			<div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl shadow-sm">
				<h3 className="text-sm font-medium text-gray-700 mb-2">
					How our AI handles a sample texting scenario
				</h3>
				<div className="flex items-center gap-3 bg-white/90 backdrop-blur-sm rounded-xl p-3 hover:bg-white transition-all duration-200">
					<button
						className="w-8 h-8 rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 flex items-center justify-center transition-all duration-200 shadow-sm"
						onClick={togglePlayPause}
						disabled={isLoading}
					>
						{isLoading ? (
							<div className="w-3 h-3 border border-white/30 border-t-white rounded-full animate-spin" />
						) : isPlaying ? (
							<Pause className="w-4 h-4 text-white" />
						) : (
							<Play className="w-4 h-4 text-white ml-0.5" />
						)}
					</button>

					<div className="flex-1 relative">
						<div className="h-1.5 bg-blue-200/40 rounded-full overflow-hidden">
							<div
								className="h-1.5 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-100"
								style={{ width: duration > 0 ? `${(currentTime / duration) * 100}%` : '0%' }}
							/>
						</div>
					</div>

					<span className="text-xs text-blue-600/70 font-medium min-w-[30px]">
						{formatTime(currentTime)}
					</span>
				</div>
			</div>

			{/* Texting Workflows Section */}
			<div className="space-y-4">
				{onboardingData.textingWorkflows?.length > 0
					? onboardingData.textingWorkflows?.map((workflow, index) => (
							<div
								key={workflow.id}
								className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl shadow-sm overflow-hidden"
							>
								<div
									className="flex items-center justify-between p-4 cursor-pointer hover:bg-white/30 transition-all duration-200"
									onClick={() => toggleWorkflowExpanded(workflow.id)}
								>
									<div className="flex items-center gap-3">
										<div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center">
											<MessageSquare className="w-4 h-4 text-white" />
										</div>
										<h3 className="font-semibold text-gray-800">
											{workflow.description || `Text Workflow ${index + 1}`}
										</h3>
									</div>
									<div className="flex items-center gap-2">
										{onboardingData.textingWorkflows?.length > 1 && (
											<Button
												size="small"
												className="rounded-full bg-red-500 border-red-500 hover:bg-red-600 hover:border-red-600 text-white"
												onClick={(e) => {
													e.stopPropagation();
													removeWorkflow(workflow.id);
												}}
											>
												<Trash className="w-4 h-4" />
											</Button>
										)}
										{expandedWorkflow === workflow.id ? (
											<ChevronUp className="w-5 h-5 text-gray-600" />
										) : (
											<ChevronDown className="w-5 h-5 text-gray-600" />
										)}
									</div>
								</div>

								{expandedWorkflow === workflow.id && (
									<div className="px-4 pb-4 space-y-4">
										{/* Scenario */}
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-2">
												When should this text be sent? <span className="text-red-500">*</span>
											</label>
											<div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 hover:bg-white transition-all duration-200">
												<TextArea
													value={workflow.description}
													onChange={(e) =>
														updateWorkflow(workflow.id, 'description', e.target.value)
													}
													placeholder="e.g., 'When customer asks about hours' or 'When customer requests a quote'"
													rows={3}
													className="border-0 shadow-none bg-transparent resize-none"
													status={
														showValidationErrors && !workflow.description.trim() ? 'error' : ''
													}
												/>
											</div>
											{showValidationErrors && !workflow.description.trim() && (
												<div className="text-red-500 text-xs mt-1">
													Please describe when this text should be sent
												</div>
											)}
										</div>

										{/* Message */}
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-2">
												What message should be sent? <span className="text-red-500">*</span>
											</label>
											<div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 hover:bg-white transition-all duration-200">
												<TextArea
													value={workflow.textMessage}
													onChange={(e) =>
														updateWorkflow(workflow.id, 'textMessage', e.target.value)
													}
													placeholder="Enter the text message to send..."
													rows={4}
													className="border-0 shadow-none bg-transparent resize-none"
													status={
														showValidationErrors && !workflow.textMessage.trim() ? 'error' : ''
													}
												/>
												<div className="text-xs text-gray-500 mt-2">
													Max 1000 characters allowed
												</div>
											</div>
											{showValidationErrors && !workflow.textMessage.trim() && (
												<div className="text-red-500 text-xs mt-1">
													Please enter a message to send
												</div>
											)}
										</div>

										{/* What should the AI do next? */}
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-2">
												What should the AI do after sending? <span className="text-red-500">*</span>
											</label>
											<div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 hover:bg-white transition-all duration-200">
												<TextArea
													value={workflow.postExecutionPrompt}
													onChange={(e) =>
														updateWorkflow(workflow.id, 'postExecutionPrompt', e.target.value)
													}
													placeholder="e.g., Tell the caller you've sent them a text message, then ask if they need anything else."
													rows={2}
													className="border-0 shadow-none bg-transparent resize-none"
													status={
														showValidationErrors && !workflow.postExecutionPrompt.trim()
															? 'error'
															: ''
													}
												/>
											</div>
											{showValidationErrors && !workflow.postExecutionPrompt.trim() && (
												<div className="text-red-500 text-xs mt-1">
													Please describe what the AI should do next
												</div>
											)}
										</div>

										{/* Advanced Options */}
										<div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 hover:bg-white transition-all duration-200">
											<h4 className="font-medium text-gray-700 mb-4">Advanced Options</h4>
											<div className="space-y-4">
												{/* Enable Short URLs */}
												<div className="flex items-center justify-between">
													<div>
														<label className="text-sm font-medium text-gray-700">
															Enable Short URLs
														</label>
														<p className="text-xs text-gray-500">
															Track analytics for links in your messages
														</p>
													</div>
													<Switch
														checked={workflow.shortURLEnabled}
														onChange={(checked) =>
															updateWorkflow(workflow.id, 'shortURLEnabled', checked)
														}
													/>
												</div>

												{/* Ask for Permission */}
												<div className="flex items-center justify-between">
													<div>
														<label className="text-sm font-medium text-gray-700">
															Ask before sending
														</label>
														<p className="text-xs text-gray-500">
															Confirm with caller before sending text
														</p>
													</div>
													<Switch
														checked={workflow.askForPermissionOnText}
														onChange={(checked) =>
															updateWorkflow(workflow.id, 'askForPermissionOnText', checked)
														}
													/>
												</div>
											</div>
										</div>
									</div>
								)}
							</div>
						))
					: null}

				<Button
					onClick={addWorkflow}
					className="w-full h-12 text-base rounded-xl border-2 border-dashed border-purple-200 text-purple-600 hover:border-purple-300 hover:bg-purple-50 transition-all duration-200"
				>
					<Plus className="w-5 h-5 mr-2" />
					Add Another Text Workflow
				</Button>
			</div>
		</div>
	);
};

export default TextingWorkflowStage;
